<script>
export default {
  name: 'AreaChart',
  props: {
    points: {
      type: Array,
      default: () => [],
    },
    chartHeight: {
      type: Number,
      default: () => 110,
    },
    showGridLines: {
      type: Boolean,
      default: () => true,
    },
  },
  data: () => {
    return {
      circleChartData: [],
      rectChartData: [],
      lineChartData: '',
      areaChartData: '',
      indicatorPosition: -10,
      primaryColor: '#3fbb79',
      gridlineColor: '#eeeeee',
      showIndicator: false,
      highlightedCircleIndex: -1,
      rectWidth: '',
      svgData: [],
      priceIncrement: '',
      maxPrice: 0,
      minPrice: 0,
    };
  },
  computed: {
    chartPadding() {
      return this.chartHeight * 0.1;
    },
    horizontalGridlinePositions() {
      const { priceIncrement, maxPrice, minPrice, calculateYValue } = this;
      if (!maxPrice || !minPrice) return;
      const coordinatesArray = [];
      let yCoordinates = 0;
      while (yCoordinates < maxPrice) {
        yCoordinates += priceIncrement;
        const isWithinRange = Math.floor(yCoordinates) <= maxPrice && yCoordinates >= minPrice;
        if (isWithinRange) {
          const value = calculateYValue(yCoordinates);
          coordinatesArray.push(value);
        }
      }
      return coordinatesArray;
    },
  },
  mounted() {
    const {
      chartHeight,
      chartPadding,
      points,
      $refs,
      drawLineChart,
      drawAreaChart,
      drawCircleChart,
      drawMouseHoverArea,
      calculateIncrement,
      calculateSvgData,
    } = this;
    if (points.length < 1) return;
    const priceArray = points.map(point => point.value);
    this.maxPrice = Math.max(...priceArray);
    this.minPrice = Math.min(...priceArray);
    this.ySpacing = (chartHeight - 2 * chartPadding) / (this.maxPrice - this.minPrice);
    const svgWidth = $refs.svgChart.clientWidth;
    const xSpacing = svgWidth / (points.length - 1);
    const svgData = calculateSvgData(xSpacing);
    drawLineChart(svgData);
    drawAreaChart(svgData);
    drawCircleChart(svgData);
    drawMouseHoverArea(svgWidth, svgData);
    calculateIncrement();
  },
  methods: {
    calculateSvgData: function(xSpacing) {
      const { points, calculateYValue } = this;
      return points.map(({ value }, index) => {
        const xValue = index * xSpacing;
        const yValue = calculateYValue(value);
        return { xValue, yValue };
      });
    },
    calculateYValue: function(value) {
      const { chartHeight, minPrice, chartPadding, ySpacing } = this;
      return chartHeight - ((value - minPrice) * ySpacing + chartPadding);
    },
    calculateIncrement: function() {
      const { maxPrice, minPrice } = this;
      const priceDifference = maxPrice - minPrice;
      const INCREMENT_OPTIONS = [0.1, 0.2, 0.5, 1, 2, 5, 10, 50, 100];
      const optionsLength = INCREMENT_OPTIONS.length;
      const INTENDED_GRID_COUNT = 3;
      for (let i = 0; i < optionsLength; i++) {
        const optionValue = INCREMENT_OPTIONS[i];
        const isMeetCondition = optionValue * INTENDED_GRID_COUNT > priceDifference;
        if (isMeetCondition) {
          this.priceIncrement = INCREMENT_OPTIONS[i];
          break;
        }
      }
    },
    drawLineChart: function(svgData) {
      // drawing line
      let lineData = '';
      svgData.map(({ xValue, yValue }, i) => {
        const command = i === 0 ? 'M' : 'L';
        lineData = lineData + ' ' + command + ' ' + xValue + ',' + yValue;
      });
      this.lineChartData = lineData;
    },
    drawAreaChart: function(svgData) {
      const areaPoints =
        this.lineChartData +
        ' L' +
        svgData[svgData.length - 1].xValue +
        ', ' +
        this.chartHeight +
        ' L' +
        svgData[0].xValue +
        ', ' +
        this.chartHeight +
        ' z';

      this.areaChartData = areaPoints;
    },
    drawCircleChart: function(svgData) {
      this.circleChartData = svgData;
    },
    drawMouseHoverArea: function(svgWidth, svgData) {
      const rectWidth = svgWidth / (svgData.length - 1);
      this.rectWidth = rectWidth;
      this.rectChartData = svgData.map((coordinates, index) => {
        const xCoordinates = (index - 0.5) * rectWidth;
        return { xCoordinates };
      });
    },
    handleMouseOver: function() {
      this.showIndicator = true;
    },
    handleMouseLeave: function() {
      this.hideOddsMeta();
    },
    handleIndicatorPosition: function(index) {
      const position = (index * this.rectWidth).toFixed(1);
      const { updatedAt, value } = this.points[index];
      this.$emit('show-tooltip', { updatedAt, value, position });
      this.highlightedCircleIndex = index;
      this.indicatorPosition = position;
    },
    handleCircleRadius: function(index) {
      return this.highlightedCircleIndex === index ? 4 : 2;
    },
    handleCircleColor: function(index) {
      return this.highlightedCircleIndex === index ? this.primaryColor : '#ffffff';
    },
    handleTouchMove: function(event) {
      try {
        const { $refs, rectWidth, handleIndicatorPosition, calculateIndicatorIndex } = this;
        const { svgChart } = $refs;
        const svgChartOffsetLeft = svgChart.getClientRects()[0].x;
        const touchOffsetLeft = event.changedTouches[0].clientX;
        const difference = touchOffsetLeft - svgChartOffsetLeft + 0.5 * rectWidth;
        const index = calculateIndicatorIndex(difference);

        handleIndicatorPosition(index);
        this.showIndicator = true;
      } catch (error) {
        console.log(error);
      }
    },
    handleTouchEnd: function() {
      this.hideOddsMeta();
    },
    hideOddsMeta: function() {
      this.showIndicator = false;
      this.highlightedCircleIndex = -1;
      this.$emit('hide-tooltip');
    },
    // This method is for getting indicator index while touch move in mobile,
    // it ensures while users swipe screen, it is identical to mouse hover in desktop.
    calculateIndicatorIndex: function(touchOffsetLeftToSVG) {
      const { rectWidth, circleChartData, $refs } = this;
      const markersNumber = circleChartData.length;
      const svgWidth = $refs.svgChart.clientWidth;
      let index = 0;

      if (touchOffsetLeftToSVG > svgWidth) {
        index = markersNumber - 1;
      } else {
        for (let i = 0, l = markersNumber; i < l; i++) {
          const rangeStartPosition = rectWidth * i;
          const rangeEndPosition = rectWidth * (i + 1);
          if (touchOffsetLeftToSVG > rangeStartPosition && touchOffsetLeftToSVG < rangeEndPosition) {
            index = i;
            break;
          }
        }
      }
      return index;
    },
  },
};
</script>

<template>
  <svg
    ref="svgChart"
    :height="chartHeight"
    class="area-chart"
    width="100%"
    @mouseover="handleMouseOver"
    @mouseleave="handleMouseLeave"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <!-- vertical gridline -->
    <template v-for="(item, index) in circleChartData">
      <rect
        v-if="showGridLines"
        :height="chartHeight"
        :x="item.xValue"
        :key="`line-vertical-${index}`"
        :fill="gridlineColor"
        class="bg-gridline bg-gridline-vertical"
        y="0"
        width="1"
      />
    </template>

    <!-- horizontal gridline -->
    <template v-for="(position, index) in horizontalGridlinePositions">
      <rect
        v-if="showGridLines"
        :key="`line-horizontal-${index}`"
        :y="position"
        :fill="gridlineColor"
        class="bg-gridline bg-gridline-horizontal"
        height="1"
        x="0"
        width="100%"
      />
    </template>

    <!-- graph line -->
    <path :d="lineChartData" 
          :stroke="primaryColor" 
          fill="none" 
          stroke-width="2" />

    <!-- graph area -->
    <path :d="areaChartData" :fill="primaryColor" style="opacity:0.2" />

    <!-- graph circle -->
    <template v-for="(circle, index) in circleChartData">
      <circle
        :cx="circle.xValue"
        :cy="circle.yValue"
        :r="handleCircleRadius(index)"
        :fill="handleCircleColor(index)"
        :stroke="primaryColor"
        :key="`circle-${index}`"
        stroke-width="2"
        class="circle"
      />
    </template>

    <!-- hover area -->
    <template v-for="(rect, index) in rectChartData">
      <rect
        :width="rectWidth"
        :height="chartHeight"
        :x="rect.xCoordinates"
        :key="`hover-area-${index}`"
        :data-index="`${index}`"
        y="0"
        fill="transparent"
        class="hover-area"
        @mouseover="handleIndicatorPosition(index)"
      />
    </template>

    <!-- hover indicator -->
    <transition name="fade">
      <rect
        v-show="showIndicator"
        :height="chartHeight"
        :fill="primaryColor"
        :x="indicatorPosition"
        y="0"
        width="1"
        class="indicator"
      />
    </transition>
  </svg>
</template>
